import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

function fmt(t) {
  const [h,m] = t.split(':').map(Number);
  return `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`;
}

const ST = {
  confirmed: { color:'#15803d', bg:'rgba(16,185,129,0.08)', dot:'#10b981' },
  pending:   { color:'#92400e', bg:'rgba(245,158,11,0.08)', dot:'#f59e0b' },
  cancelled: { color:'#64748b', bg:'rgba(148,163,184,0.08)', dot:'#94a3b8' },
  no_show:   { color:'#7c3aed', bg:'rgba(124,58,237,0.08)', dot:'#7c3aed' },
};

// All reservation times are interpreted as GMT+2
function resTime(date, time) { return new Date(date + 'T' + time + '+02:00'); }
function isPast(date, time)   { return resTime(date, time) < new Date(); }
function isExpired(date, time){ return resTime(date, time) < new Date(Date.now() - 24 * 60 * 60 * 1000); }
function canMarkNoShow(date, time, status) {
  if (!['confirmed','pending'].includes(status)) return false;
  return resTime(date, time) < new Date(Date.now() - 30 * 60 * 1000);
}
function fmtBookedOn(ts) {
  if (!ts) return '—';
  // SQLite stores UTC without 'Z' — append it, then shift to GMT+2
  const d = new Date(ts.replace(' ', 'T') + 'Z');
  const g = new Date(d.getTime() + 2 * 60 * 60 * 1000);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${g.getUTCDate()} ${months[g.getUTCMonth()]}, ${String(g.getUTCHours()).padStart(2,'0')}:${String(g.getUTCMinutes()).padStart(2,'0')}`;
}

export default function ReservationsList({ onNavigate }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({date:'',status:'',source:'',search:''});
  const [sel, setSel]         = useState(null);
  const [editing, setEditing] = useState(false);
  const [ed, setEd]           = useState({});
  const [saving, setSaving]   = useState(false);
  const [confirming, setConfirming] = useState(null); // 'cancel' | 'noshow'

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const p = Object.fromEntries(Object.entries(filters).filter(([,v])=>v));
      const all = await api.getReservations(p);
      // Hide reservations that have expired (>24h past) unless a date filter is active
      const filtered = filters.date || filters.status === 'no_show'
        ? all
        : all.filter(r => !isExpired(r.date, r.time_slot));
      setRows(filtered);
    } catch(_){} finally { setLoading(false); }
  }, [filters]);

  useEffect(()=>{ load(true); },[load]);
  useEffect(()=>{
    const refresh = () => load(false);
    window.addEventListener('seva:refresh', refresh);
    return ()=>window.removeEventListener('seva:refresh', refresh);
  },[load]);

  const open = async r => {
    const full = await api.getReservation(r.id);
    setSel(full); setEd(full); setEditing(false);
    if (r.seen === 0) {
      await api.markSeen(r.id);
      setRows(prev => prev.map(x => x.id === r.id ? { ...x, seen: 1 } : x));
      window.dispatchEvent(new CustomEvent('seva:unseen-update'));
    }
  };
  const save = async () => {
    setSaving(true);
    try { await api.updateReservation(sel.id,ed); setEditing(false); await load(); const u = await api.getReservation(sel.id); setSel(u); setEd(u); }
    catch(e){ alert(e.message); } finally { setSaving(false); }
  };
  const cancel  = () => setConfirming('cancel');
  const noShow  = () => setConfirming('noshow');

  const executeConfirmed = async () => {
    if (confirming === 'cancel') await api.cancelReservation(sel.id);
    if (confirming === 'noshow') await api.markNoShow(sel.id);
    setConfirming(null); setSel(null); load();
  };

  const unmarkNoShow = async () => {
    await api.updateReservation(sel.id, { status: 'confirmed' });
    setSel(null); load();
  };

  const closeModal = () => { setSel(null); setConfirming(null); };
  const setF = (k,v) => setFilters(f=>({...f,[k]:v}));

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>Reservations</h1>
          <p style={s.sub}>View, search and manage all bookings.</p>
        </div>
        <button style={s.addBtn} className="glass-btn t" onClick={()=>onNavigate('new')}>+ Add Booking</button>
      </div>

      <div style={s.filterBar}>
        <input style={s.fi} className="glass-input" type="date" value={filters.date} onChange={e=>setF('date',e.target.value)} />
        <select style={s.fi} className="glass-input" value={filters.status} onChange={e=>setF('status',e.target.value)}>
          <option value="">All statuses</option>
          <option value="confirmed">Confirmed</option><option value="pending">Pending</option><option value="cancelled">Cancelled</option>
        </select>
        <select style={s.fi} className="glass-input" value={filters.source} onChange={e=>setF('source',e.target.value)}>
          <option value="">All sources</option>
          <option value="ai">AI chatbot</option><option value="manual">Manual</option>
        </select>
        <input style={{...s.fi,flex:1}} className="glass-input" type="text" placeholder="Search name or phone…" value={filters.search} onChange={e=>setF('search',e.target.value)} />
        {Object.values(filters).some(Boolean) && <button style={s.clearBtn} onClick={()=>setFilters({date:'',status:'',source:'',search:''})}>Clear</button>}
      </div>

      {loading ? <div style={{color:'var(--text-muted)',padding:20}}>Loading…</div>
      : rows.length === 0 ? <div style={s.empty}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={{marginBottom:10}}>
            <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><path d="M13 3h-2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z" /><path d="M9 12h6M9 16h4" />
          </svg>
          <div>No reservations found</div>
        </div>
      : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead><tr>{['Date','Time','Name','Party','Table','Status','Source','Booked on','Confirmation'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map(r=>(
                <tr key={r.id} onClick={()=>open(r)} className="glass-tr-hover" style={r.seen===0?{background:'rgba(16,185,129,0.14)',borderLeft:'3px solid #10b981'}:isPast(r.date,r.time_slot)?{opacity:0.45}:{}}>
                  <td style={s.td}>{r.date}</td>
                  <td style={s.td}>{fmt(r.time_slot)}</td>
                  <td style={{...s.td,fontWeight:700,color:'var(--text-primary)'}}>
                    {r.seen===0 && <span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:'#10b981',marginRight:6,flexShrink:0,verticalAlign:'middle'}} />}
                    {r.customer_name}
                  </td>
                  <td style={s.td}>{r.party_size}</td>
                  <td style={s.td}>{r.tables_display || r.table_label}</td>
                  <td style={s.td}>
                    <span style={{...s.badge,color:(ST[r.status]||ST.confirmed).color,background:(ST[r.status]||ST.confirmed).bg}}>
                      <span style={{...s.dot,background:(ST[r.status]||ST.confirmed).dot}} />{r.status}
                    </span>
                  </td>
                  <td style={s.td}>
                    {r.source==='ai'?<span style={s.aiTag}>AI</span>:<span style={s.manTag}>Manual</span>}
                    {r.special_occasion && <span style={s.occasionTag} title={r.special_occasion}> 🎉</span>}
                  </td>
                  <td style={{...s.td,fontFamily:'monospace',fontSize:11,color:'var(--text-muted)'}}>{fmtBookedOn(r.created_at)}</td>
                  <td style={{...s.td,fontFamily:'monospace',fontSize:11,color:'var(--text-muted)'}}>{r.confirmation_number}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sel && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.modalTop}>
              <div>
                <h2 style={s.modalName}>{sel.customer_name}</h2>
                <div style={s.modalSub}>{fmt(sel.time_slot)} · {sel.party_size} guests · {sel.table_label}</div>
              </div>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                {!editing && sel.status !== 'cancelled' && !confirming && (
                  <>
                    <button style={s.editBtn} className="glass-btn t" onClick={()=>setEditing(true)}>Edit</button>
                    {canMarkNoShow(sel.date, sel.time_slot, sel.status) && (
                      <button style={s.noShowBtn} className="glass-btn t" onClick={noShow}>No-show</button>
                    )}
                    {sel.status === 'no_show' && (
                      <button style={s.undoBtn} className="glass-btn t" onClick={unmarkNoShow}>Undo no-show</button>
                    )}
                    {['confirmed','pending'].includes(sel.status) && (
                      <button style={s.cancelResBtn} className="glass-btn t" onClick={cancel}>Cancel</button>
                    )}
                  </>
                )}
                <button style={s.closeBtn} onClick={closeModal}>×</button>
              </div>
            </div>

            {/* Inline confirmation — replaces window.confirm */}
            {confirming && (
              <div style={s.confirmBanner}>
                <span style={{fontSize:14,fontWeight:600,color:'#0f172a'}}>
                  {confirming === 'cancel' ? 'Cancel this reservation?' : 'Mark as no-show?'}
                </span>
                <div style={{display:'flex',gap:8}}>
                  <button style={s.confirmYes} onClick={executeConfirmed}>
                    {confirming === 'cancel' ? 'Yes, cancel' : 'Yes, no-show'}
                  </button>
                  <button style={s.confirmNo} onClick={()=>setConfirming(null)}>Go back</button>
                </div>
              </div>
            )}

            <div style={s.divider} />
            {editing ? (
              <div style={s.editForm}>
                {[['customer_name','Name','text'],['phone','Phone','text'],['email','Email','email'],
                  ['date','Date','date'],['time_slot','Time','time'],['party_size','Party size','number'],
                  ['dietary_restrictions','Dietary','text'],['special_requests','Special requests','text'],['notes','Notes','text']]
                .map(([field,label,type])=>(
                  <div key={field} style={s.efRow}>
                    <label style={s.efLabel}>{label}</label>
                    <input style={s.efInput} className="glass-input" type={type} value={ed[field]||''} onChange={e=>setEd(d=>({...d,[field]:e.target.value}))} />
                  </div>
                ))}
                <div style={s.efRow}>
                  <label style={s.efLabel}>Status</label>
                  <select style={s.efInput} className="glass-input" value={ed.status} onChange={e=>setEd(d=>({...d,status:e.target.value}))}>
                    <option value="confirmed">Confirmed</option><option value="pending">Pending</option><option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div style={{display:'flex',gap:10,marginTop:8}}>
                  <button style={s.saveBtn} className="glass-btn t" onClick={save} disabled={saving}>{saving?'Saving…':'Save changes'}</button>
                  <button style={s.discBtn} className="glass-btn t" onClick={()=>{setEditing(false);setEd(sel);}}>Discard</button>
                </div>
              </div>
            ) : (
              <dl style={s.grid}>
                {[['Confirmation',sel.confirmation_number],['Date',sel.date],['Time',fmt(sel.time_slot)],
                  ['Party',sel.party_size+' guests'],['Table',sel.tables_display||sel.table_label],['Phone',sel.phone],
                  ['Email',sel.email||'—'],['Status',sel.status],['Source',sel.source],
                  sel.special_occasion ? ['Occasion', '🎉 '+sel.special_occasion] : null,
                  ['Dietary',sel.dietary_restrictions||'—'],['Requests',sel.special_requests||'—'],['Notes',sel.notes||'—']]
                .filter(Boolean)
                .map(([k,v])=>(
                  <React.Fragment key={k}><dt style={s.dt}>{k}</dt><dd style={s.dd}>{v}</dd></React.Fragment>
                ))}
              </dl>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  header:   { display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24 },
  h1:       { fontSize:28, fontWeight:800, color:'var(--text-primary)', margin:0, letterSpacing:'-0.5px' },
  sub:      { fontSize:14, color:'var(--text-muted)', marginTop:4 },
  addBtn:   { background:'var(--accent)', border:'none', borderRadius:10, padding:'11px 20px', cursor:'pointer', fontSize:14, fontWeight:700, color:'#fff', flexShrink:0 },
  filterBar:{ display:'flex', gap:10, marginBottom:18, flexWrap:'wrap', alignItems:'center' },
  fi:       { background:'var(--input-bg)', border:'1px solid var(--input-border)', borderRadius:8, padding:'8px 12px', fontSize:13, outline:'none', color:'var(--input-text)' },
  clearBtn: { background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:13, textDecoration:'underline' },
  empty:    { color:'var(--text-muted)', textAlign:'center', padding:'60px 0', fontSize:15 },
  tableWrap:{ background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:18, overflow:'hidden', boxShadow:'0 1px 4px rgba(15,23,42,0.06)' },
  table:    { width:'100%', borderCollapse:'collapse' },
  th:       { padding:'12px 16px', fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', textAlign:'left', background:'var(--section-bg)', borderBottom:'1px solid var(--section-border)' },
  td:       { padding:'13px 16px', fontSize:13, color:'var(--text-secondary)', borderBottom:'1px solid var(--divider)' },
  badge:    { display:'inline-flex', alignItems:'center', gap:5, fontWeight:600, fontSize:11, borderRadius:20, padding:'3px 9px' },
  dot:      { width:5, height:5, borderRadius:'50%', flexShrink:0 },
  aiTag:      { fontSize:12, color:'#4f46e5', fontWeight:600 },
  manTag:     { fontSize:12, color:'var(--text-muted)' },
  occasionTag:{ fontSize:13, cursor:'default' },
  overlay:  { position:'fixed', inset:0, background:'rgba(15,23,42,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500 },
  modal:    { background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:22, padding:30, width:520, maxWidth:'90vw', maxHeight:'85vh', overflow:'auto', boxShadow:'0 20px 60px rgba(15,23,42,0.18)' },
  modalTop: { display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:18, gap:12 },
  modalName:{ fontSize:20, fontWeight:800, color:'var(--text-primary)', margin:'0 0 4px' },
  modalSub: { fontSize:13, color:'var(--text-muted)' },
  editBtn:  { background:'var(--accent)', border:'none', borderRadius:8, padding:'7px 16px', cursor:'pointer', fontSize:13, fontWeight:600, color:'#fff' },
  noShowBtn:   { background:'#f5f3ff', border:'1px solid #ddd6fe', borderRadius:8, padding:'7px 16px', cursor:'pointer', fontSize:13, color:'#7c3aed' },
  undoBtn:     { background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, padding:'7px 16px', cursor:'pointer', fontSize:13, color:'#15803d' },
  confirmBanner:{ background:'#fef9ec', border:'1px solid #fde68a', borderRadius:12, padding:'14px 18px', margin:'0 0 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' },
  confirmYes:  { background:'#dc2626', border:'none', borderRadius:8, padding:'7px 16px', fontSize:13, fontWeight:700, color:'#fff', cursor:'pointer' },
  confirmNo:   { background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'7px 16px', fontSize:13, fontWeight:600, color:'#475569', cursor:'pointer' },
  cancelResBtn:{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:'7px 16px', cursor:'pointer', fontSize:13, color:'#dc2626' },
  closeBtn: { background:'var(--section-bg)', border:'1px solid var(--section-border)', borderRadius:8, width:32, height:32, fontSize:20, cursor:'pointer', color:'var(--text-secondary)', flexShrink:0 },
  divider:  { height:1, background:'var(--divider)', margin:'0 0 18px' },
  grid:     { display:'grid', gridTemplateColumns:'140px 1fr', gap:'11px 14px' },
  dt:       { fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' },
  dd:       { fontSize:13, color:'var(--text-secondary)' },
  editForm: { display:'flex', flexDirection:'column', gap:0 },
  efRow:    { display:'flex', flexDirection:'column', marginBottom:14 },
  efLabel:  { fontSize:11, fontWeight:700, color:'var(--text-muted)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' },
  efInput:  { background:'var(--input-bg)', border:'1px solid var(--input-border)', borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', color:'var(--input-text)' },
  saveBtn:  { background:'var(--accent)', border:'none', borderRadius:9, padding:'10px 20px', cursor:'pointer', fontSize:14, fontWeight:700, color:'#fff' },
  discBtn:  { background:'var(--section-bg)', border:'1px solid var(--section-border)', borderRadius:9, padding:'10px 20px', cursor:'pointer', fontSize:14, color:'var(--text-secondary)' },
};
